import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle2, Clock, Upload, X, FileText, Image as ImageIcon, Globe, Instagram, Facebook, Youtube } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_OPTIONS = ["Bodywork", "Energy healing", "Breathwork", "Yoga", "Meditation", "Nutrition", "Herbalism", "Coaching", "Sound healing", "Counseling", "Movement", "Musician", "Crafter", "Homesteader", "Artisan"];

export default function EnhancerApply() {
  const navigate = useNavigate();
  const imgInput = useRef(null);
  const certInput = useRef(null);
  const [f, setF] = useState({
    business_name: "", bio: "", credentials: "", path_story: "", mission: "",
    categories: "", website: "",
    social_links: { instagram: "", facebook: "", youtube: "", tiktok: "", x: "" },
  });
  const [images, setImages] = useState([]);
  const [certs, setCerts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["my-enhancer-app"],
    queryFn: () => api.get("/vendor/me/application?type=enhancer").then((r) => r.data),
    retry: false,
  });

  const set = (k, v) => setF({ ...f, [k]: v });
  const setSocial = (k, v) => setF({ ...f, social_links: { ...f.social_links, [k]: v } });
  const app = data?.application;

  const selectedCats = f.categories.split(",").map((c) => c.trim()).filter(Boolean);
  const toggleCat = (c) => {
    const has = selectedCats.some((x) => x.toLowerCase() === c.toLowerCase());
    const next = has ? selectedCats.filter((x) => x.toLowerCase() !== c.toLowerCase()) : [...selectedCats, c];
    set("categories", next.join(", "));
  };

  const doUpload = async (files, endpoint, onDone) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onDone(r.data);
      }
      toast.success("Uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "We couldn't upload that file. Please check it and try again."); }
    finally { setUploading(false); }
  };
  const onImages = (e) => doUpload(e.target.files, "/uploads/image", (d) => setImages((p) => [...p, d.url].slice(0, 12)));
  const onCerts = (e) => doUpload(e.target.files, "/uploads/file", (d) => setCerts((p) => [...p, { url: d.url, name: d.name }].slice(0, 12)));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name.trim()) return toast.error("Please enter your name or practice name.");
    setSubmitting(true);
    try {
      await api.post("/vendor/apply", {
        applicant_type: "enhancer",
        business_name: f.business_name, description: f.bio, bio: f.bio,
        credentials: f.credentials, path_story: f.path_story, mission: f.mission,
        website: f.website, social_links: f.social_links,
        categories: selectedCats, images, certifications: certs,
      });
      toast.success("Application submitted! We'll review it soon.");
      refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const field = (label, key, ph, rows = 4) => (
    <div>
      <label className="text-sm font-medium text-[#1A242B] mb-1 block">{label}</label>
      <textarea rows={rows} value={f[key]} onChange={(e) => set(key, e.target.value)} data-testid={`enhancer-${key}`} className="eyw-input w-full resize-none" placeholder={ph} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F7F3] py-16 px-6" data-testid="enhancer-apply-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-[#9E5C7F] text-sm font-medium"><Sparkles size={16} /> Become an Enhancer</div>
        <h1 className="font-serif text-4xl text-[#1A242B] mt-2 mb-3">Share your gifts with our community</h1>
        <p className="text-[#4A5D6B] mb-3">Enhancers offer their practice, wisdom and healing to our members. Tell us your story, your training, and how you help others and the planet. Approved Enhancers get a full practitioner profile, booking tools, and seller access.</p>
        <p className="text-sm text-[#8FA89B] mb-8">Just want to sell products? <Link to="/vendor/apply" className="text-[#144F75] hover:underline">Apply as a Vendor instead →</Link></p>

        {isLoading ? <div className="p-8 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /></div>
          : data?.is_enhancer ? (
            <div className="eyw-card p-8 text-center" data-testid="enhancer-already">
              <CheckCircle2 className="mx-auto text-[#5C8A72]" size={40} />
              <h2 className="font-serif text-2xl mt-3">You're an approved Enhancer 🎉</h2>
              <p className="text-[#4A5D6B] mt-2">Head to your dashboard to set up your offerings and profile.</p>
              <button onClick={() => navigate("/enhancer")} data-testid="enhancer-go-dashboard" className="eyw-btn mt-5">Go to Dashboard</button>
            </div>
          ) : app?.status === "pending" ? (
            <div className="eyw-card p-8 text-center" data-testid="enhancer-pending">
              <Clock className="mx-auto text-[#B5794E]" size={40} />
              <h2 className="font-serif text-2xl mt-3">Application under review</h2>
              <p className="text-[#4A5D6B] mt-2">Thanks! We received your Enhancer application. We'll email you once it's reviewed.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="eyw-card p-7 space-y-5" data-testid="enhancer-apply-form">
              {app?.status === "rejected" && <div className="text-sm text-[#B45151] bg-[#B45151]/10 rounded-lg px-3 py-2">Your previous application wasn't approved{app.reason ? `: ${app.reason}` : ""}. You're welcome to apply again.</div>}
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Your name or practice name *</label>
                <input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="enhancer-name" className="eyw-input w-full" placeholder="e.g. Maya Rivers · Rooted Healing Arts" />
              </div>

              {field("A short bio — tell us about you", "bio", "Who you are, your background, and what you offer…", 4)}
              {field("Credentials & training", "credentials", "Certifications, schools, lineages, years of practice…", 3)}
              {field("Why do you walk the way you do?", "path_story", "The path and philosophy behind your work…", 4)}
              {field("How do you intend to help others and the planet?", "mission", "Your mission and the impact you hope to create…", 4)}

              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">What do you practice / offer?</label>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="enhancer-category-chips">
                  {CATEGORY_OPTIONS.map((c) => {
                    const active = selectedCats.some((x) => x.toLowerCase() === c.toLowerCase());
                    return (
                      <button type="button" key={c} onClick={() => toggleCat(c)} data-testid={`enhancer-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-[#9E5C7F] text-white border-[#9E5C7F]" : "bg-white text-[#4A5D6B] border-[#E5E0D8] hover:border-[#9E5C7F]"}`}>{c}</button>
                    );
                  })}
                </div>
                <input value={f.categories} onChange={(e) => set("categories", e.target.value)} data-testid="enhancer-categories" className="eyw-input w-full" placeholder="Tap above or type your own, comma-separated" />
              </div>

              {/* Photos */}
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Photos of you & your work</label>
                <div className="flex flex-wrap gap-3">
                  {images.map((url, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#E5E0D8]" data-testid={`enhancer-image-${i}`}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))} data-testid={`enhancer-image-remove-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => imgInput.current?.click()} disabled={uploading} data-testid="enhancer-image-upload" className="w-24 h-24 rounded-lg border-2 border-dashed border-[#C9BFA8] grid place-items-center text-[#8FA89B] hover:border-[#9E5C7F] hover:text-[#9E5C7F] transition-colors">
                    {uploading ? <Loader2 className="animate-spin" size={20} /> : <ImageIcon size={22} />}
                  </button>
                  <input ref={imgInput} type="file" accept="image/*" multiple hidden onChange={onImages} />
                </div>
              </div>

              {/* Certifications */}
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Certifications (PDF or image)</label>
                <div className="space-y-2">
                  {certs.map((c, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#F1EEE8] rounded-lg px-3 py-2 text-sm" data-testid={`enhancer-cert-${i}`}>
                      <a href={c.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#144F75] truncate"><FileText size={14} /> {c.name}</a>
                      <button type="button" onClick={() => setCerts(certs.filter((_, x) => x !== i))} data-testid={`enhancer-cert-remove-${i}`} className="text-[#B45151]"><X size={14} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => certInput.current?.click()} disabled={uploading} data-testid="enhancer-cert-upload" className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-dashed border-[#C9BFA8] text-[#4A5D6B] hover:border-[#9E5C7F] hover:text-[#9E5C7F] transition-colors">
                    <Upload size={15} /> Upload certification
                  </button>
                  <input ref={certInput} type="file" accept=".pdf,image/*" multiple hidden onChange={onCerts} />
                </div>
              </div>

              {/* Links & social */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#1A242B] block">Website & social media</label>
                <div className="flex items-center gap-2 eyw-input"><Globe size={15} className="text-[#8FA89B]" /><input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="enhancer-website" className="bg-transparent outline-none flex-1 text-sm" placeholder="https://your-website.com" /></div>
                <div className="flex items-center gap-2 eyw-input"><Instagram size={15} className="text-[#8FA89B]" /><input value={f.social_links.instagram} onChange={(e) => setSocial("instagram", e.target.value)} data-testid="enhancer-instagram" className="bg-transparent outline-none flex-1 text-sm" placeholder="Instagram URL or @handle" /></div>
                <div className="flex items-center gap-2 eyw-input"><Facebook size={15} className="text-[#8FA89B]" /><input value={f.social_links.facebook} onChange={(e) => setSocial("facebook", e.target.value)} data-testid="enhancer-facebook" className="bg-transparent outline-none flex-1 text-sm" placeholder="Facebook URL" /></div>
                <div className="flex items-center gap-2 eyw-input"><Youtube size={15} className="text-[#8FA89B]" /><input value={f.social_links.youtube} onChange={(e) => setSocial("youtube", e.target.value)} data-testid="enhancer-youtube" className="bg-transparent outline-none flex-1 text-sm" placeholder="YouTube channel URL" /></div>
                <p className="text-xs text-[#8FA89B]">We'll link these on your profile — and soon you'll be able to cross-post from our site to each platform.</p>
              </div>

              <button type="submit" disabled={submitting || uploading} data-testid="enhancer-submit" className="eyw-btn w-full !bg-[#9E5C7F] hover:!bg-[#8a4e6f] disabled:opacity-60">
                {submitting && <Loader2 className="animate-spin" size={16} />} Submit Enhancer application
              </button>
            </form>
          )}
      </div>
    </div>
  );
}
